import {
  FUTURE_DATE_ERROR_MESSAGE,
  INVALID_DATE_ERROR_MESSAGE,
} from '@/common/constants/constants';
import { TextField, Typography } from '@eventbrite/marmalade';
import { isValid } from 'date-fns'; // Import format for displaying the parsed date
import React, { useState } from 'react';
import styles from './Datepicker.module.scss';

interface DatePickerProps {
  date: string;
  setDate: (date: string) => void;
  isDisabled: boolean;
  validation?: string;
}

const DatePicker: React.FC<DatePickerProps> = ({ isDisabled, date, setDate, validation }) => {
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    value: string,
    name: string,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const sanitisedDate = value.replace(/\D/g, '');

    // Format the date with slashes
    let formattedDate = sanitisedDate;
    if (sanitisedDate.length > 2) {
      formattedDate = `${sanitisedDate.substring(0, 2)}/${sanitisedDate.substring(2)}`;
    }
    if (sanitisedDate.length > 4) {
      formattedDate = `${sanitisedDate.substring(0, 2)}/${sanitisedDate.substring(2, 4)}/${sanitisedDate.substring(4)}`;
    }

    setDate(formattedDate);

    // Validate the formatted value only if it is complete
    if (formattedDate.length >= 10) {
      const [month, day, year] = formattedDate.split('/').map(Number);
      const parsedDate = new Date(formattedDate);
      const today = new Date();

      if (isValid(parsedDate)) {
        if (parsedDate <= today) {
          setError(null);
        } else {
          setError(FUTURE_DATE_ERROR_MESSAGE);
        }
      } else {
        setError(INVALID_DATE_ERROR_MESSAGE);
      }
    } else {
      setError(null); // Clear error if the date is incomplete
    }
  };

  return (
    <div>
      <TextField
        data-testid="date_of_purchase"
        id="date_of_purchase"
        label="Date of purchase"
        required
        type="text"
        placeholder={'MM/DD/YYYY'}
        onChange={handleChange}
        hasError={!!error || !!validation}
        helperText={validation}
        value={date}
        crossOrigin={undefined}
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
        disabled={isDisabled}
      />
      {error && (
        <Typography variant="body-sm" className={styles.error}>
          {error}
        </Typography>
      )}
      <Typography variant="body-sm">If you&apos;re not sure, check your bank statement</Typography>
    </div>
  );
};

export default DatePicker;
